import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Field, InputSuelto, SelectSuelto } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  AUTOMATICOS, abrirPrevisualizacion, useGuardarCampos, useGuardarPlantilla,
  usePaginaDePlantilla, usePlantilla, useSubirPdf, useTamanosDePagina,
  type CampoDePlantilla,
} from './api';
import { FormularioDePlantilla } from './FormularioDePlantilla';

/**
 * Configurador — dónde va cada dato sobre el PDF oficial.
 * `#documentos/plantilla/<id>`.
 *
 * ## El defecto que esta pantalla arregla
 *
 * **La imagen del PDF nunca cargaba.** El vanilla la pintaba con
 *
 *     <img src="/api/documents/templates/N/page/0">
 *
 * y esa ruta exige rol de gestión (`CoordOrAdmin` → `OAuth2PasswordBearer`).
 * Una etiqueta `<img>` **no manda la cabecera `Authorization`**, así que el
 * servidor respondía **401** —comprobado con una petición real el
 * 2026-09-24— y lo que se veía era la imagen rota. Encima de esa imagen rota se
 * pulsaba para colocar los campos, y las coordenadas salían de
 * `getBoundingClientRect()` de una caja que ya no era la página.
 *
 * Cuadra con lo que hay en la base: las dos plantillas publicadas tienen
 * **cero campos configurados**. No es que nadie quisiera; es que no se podía.
 *
 * Lo mismo le pasaba a «Previsualizar», que abría la ruta `/preview` en otra
 * pestaña: 401 y el JSON del error a la vista.
 *
 * ## Lo demás que cambia
 *
 * - **Se puede colocar y mover un campo con el teclado.** Antes solo existía
 *   `img.onclick`: sin ratón no había forma de añadir uno, y para mover cualquiera
 *   había que borrarlo y volver a pulsar.
 * - **Publicar comprueba que el guardado funcionó.** `await saveFields()` no
 *   devolvía nada, así que si el `PUT` fallaba se publicaba la versión anterior
 *   sin avisar.
 * - **Si no se pueden leer los tamaños de página, se dice.** Era un `catch {}`
 *   vacío y el lienzo caía a tamaño carta: los campos quedaban colocados sobre
 *   una página que no era la real.
 */
export function Configurador({ id }: { id: number }) {
  const consulta = usePlantilla(id);
  const plantilla = consulta.data;
  const tienePdf = !!plantilla?.has_pdf;

  const tamanos = useTamanosDePagina(id, tienePdf);
  const guardarCampos = useGuardarCampos();
  const guardarPlantilla = useGuardarPlantilla();
  const subir = useSubirPdf();

  const [campos, setCampos] = useState<CampoDePlantilla[]>([]);
  const [pagina, setPagina] = useState(0);
  const [elegido, setElegido] = useState<number | null>(null);
  const [editandoDatos, setEditandoDatos] = useState(false);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [error, setError] = useState('');
  const cargados = useRef(false);

  useEffect(() => {
    if (!plantilla || cargados.current) return;
    setCampos((plantilla.fields ?? []).map((f) => ({ ...f })));
    cargados.current = true;
  }, [plantilla]);

  const paginas = tamanos.data?.sizes.length || plantilla?.page_count || 1;
  const tamano = tamanos.data?.sizes[pagina] ?? { w: 612, h: 792 };

  function agregar(x: number, y: number) {
    const n = campos.length + 1;
    setCampos((cs) => [...cs, {
      name: `campo_${n}`, label: `Campo ${n}`, type: 'text', source: 'manual',
      page: pagina, x: Math.round(x), y: Math.round(y), size: 10, required: false,
    }]);
    setElegido(campos.length);
  }

  function cambiar(i: number, parche: Partial<CampoDePlantilla>) {
    setCampos((cs) => cs.map((c, j) => (j === i ? { ...c, ...parche } : c)));
  }

  /** Guarda y dice si funcionó. El vanilla no lo decía, y publicar encima de un
   *  guardado fallido publicaba la versión anterior. */
  async function guardar(): Promise<boolean> {
    try {
      const r = await guardarCampos.mutateAsync({ id, campos });
      Utils.toast(`${r.count} ${r.count === 1 ? 'campo guardado' : 'campos guardados'}`, 'success');
      return true;
    } catch (err) {
      Utils.toast(err instanceof Error ? err.message : 'No se pudieron guardar los campos', 'error');
      return false;
    }
  }

  if (consulta.isPending) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (consulta.isError || !plantilla) {
    return (
      <EstadoError
        err={consulta.error}
        que="la plantilla"
        onReintentar={() => void consulta.refetch()}
        onVolver={() => { location.hash = '#documentos/plantillas'; }}
      />
    );
  }

  return (
    <>
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <a
          href="#documentos/plantillas"
          className="inline-flex items-center gap-1 text-3 font-semibold text-brand-ink hover:underline focus-visible:focus-anillo"
        >
          <i className="bi bi-arrow-left" aria-hidden="true" /> Plantillas
        </a>
        <h1 className="min-w-0 truncate text-5 font-bold text-brand-ink">
          {plantilla.name || '(sin nombre)'}
        </h1>
        <Badge tone={plantilla.status === 'publicado' ? 'ok' : 'warn'}>{plantilla.status}</Badge>

        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditandoDatos(true)}>
            <i className="bi bi-pencil" aria-hidden="true" /> Datos
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!tienePdf}
            razonDeshabilitado="Sube primero el PDF oficial"
            onClick={async () => {
              if (!(await guardar())) return;
              try {
                await abrirPrevisualizacion(id);
              } catch (err) {
                Utils.toast(
                  err instanceof Error ? err.message : 'No se pudo previsualizar', 'error');
              }
            }}
          >
            <i className="bi bi-eye" aria-hidden="true" /> Previsualizar
          </Button>
          <Button
            size="sm"
            loading={guardarCampos.isPending}
            disabled={!tienePdf}
            razonDeshabilitado="Sube primero el PDF oficial"
            onClick={() => void guardar()}
          >
            <i className="bi bi-save" aria-hidden="true" /> Guardar campos
          </Button>
          {plantilla.status !== 'publicado' && (
            <Button
              variant="secondary"
              size="sm"
              loading={guardarPlantilla.isPending}
              disabled={!tienePdf}
              razonDeshabilitado="Una plantilla sin PDF no se puede publicar"
              onClick={async () => {
                // Publicar lo último que se ve, no lo último que se guardó bien.
                if (!(await guardar())) return;
                guardarPlantilla.mutate(
                  {
                    id,
                    cuerpo: {
                      name: plantilla.name,
                      description: plantilla.description ?? null,
                      status: 'publicado',
                    },
                  },
                  {
                    onSuccess: () => Utils.toast('Plantilla publicada', 'success'),
                    onError: (err) => Utils.toast(
                      err instanceof Error ? err.message : 'No se pudo publicar', 'error'),
                  },
                );
              }}
            >
              <i className="bi bi-broadcast" aria-hidden="true" /> Publicar
            </Button>
          )}
        </div>
      </header>

      {!tienePdf ? (
        <Card className="flex flex-col gap-3 p-4">
          <p className="rounded-sm border border-info/40 bg-info/12 p-3 text-3 text-ink-2">
            <i className="bi bi-info-circle" aria-hidden="true" /> Sube el{' '}
            <strong>PDF oficial</strong> aprobado por Calidad. No se modifica su diseño: solo se
            estampan los datos en las posiciones que configures.
          </p>
          <Field label="PDF oficial" error={error || undefined}>
            <input
              type="file"
              accept="application/pdf"
              className="block w-full text-3 text-ink-2 file:mr-3 file:rounded-sm file:border-0 file:bg-brand file:px-3 file:py-2 file:text-3 file:font-semibold file:text-on-brand hover:file:bg-brand-strong"
              onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); setError(''); }}
            />
          </Field>
          <div>
            <Button
              size="sm"
              loading={subir.isPending}
              disabled={!archivo}
              razonDeshabilitado="Elige primero el archivo"
              onClick={() => {
                if (!archivo) return;
                subir.mutate({ id, archivo }, {
                  onSuccess: (r) => {
                    Utils.toast('PDF cargado', 'success');
                    // Los campos del AcroForm, si el PDF los trae y no había
                    // ninguno configurado todavía.
                    const sug = r.suggested_fields ?? [];
                    if (sug.length && !campos.length) {
                      setCampos(sug.map((s, i) => ({
                        name: s.name || `campo_${i + 1}`,
                        label: s.name || `Campo ${i + 1}`,
                        type: 'text' as const,
                        source: 'manual' as const,
                        page: s.page ?? 0,
                        x: s.x, y: s.y, size: s.size ?? 10, required: false,
                      })));
                      Utils.toast(
                        `${sug.length} campo(s) propuestos desde el PDF — revísalos y guarda`,
                        'info');
                    }
                  },
                  onError: (err) => setError(
                    err instanceof Error ? err.message : 'No se pudo subir el PDF'),
                });
              }}
            >
              <i className="bi bi-upload" aria-hidden="true" /> Subir el PDF oficial
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr_20rem] lg:items-start">
          <Lienzo
            id={id}
            pagina={pagina}
            paginas={paginas}
            tamano={tamano}
            tamanosFallaron={tamanos.isError}
            campos={campos}
            elegido={elegido}
            onPagina={setPagina}
            onColocar={agregar}
            onElegir={setElegido}
            onMover={(i, dx, dy) => cambiar(i, {
              x: Math.max(0, Math.min(tamano.w, (campos[i]?.x ?? 0) + dx)),
              y: Math.max(0, Math.min(tamano.h, (campos[i]?.y ?? 0) + dy)),
            })}
          />

          <ListaDeCampos
            campos={campos}
            pagina={pagina}
            elegido={elegido}
            onElegir={setElegido}
            onCambiar={cambiar}
            onQuitar={(i) => {
              setCampos((cs) => cs.filter((_, j) => j !== i));
              setElegido(null);
            }}
            onAgregarAlCentro={() => agregar(tamano.w / 2, tamano.h / 2)}
          />
        </div>
      )}

      {editandoDatos && (
        <FormularioDePlantilla plantilla={plantilla} onCerrar={() => setEditandoDatos(false)} />
      )}
    </>
  );
}

/* ── El lienzo ─────────────────────────────────────────────────────────── */

function Lienzo({
  id, pagina, paginas, tamano, tamanosFallaron, campos, elegido,
  onPagina, onColocar, onElegir, onMover,
}: {
  id: number;
  pagina: number;
  paginas: number;
  tamano: { w: number; h: number };
  tamanosFallaron: boolean;
  campos: CampoDePlantilla[];
  elegido: number | null;
  onPagina: (n: number) => void;
  onColocar: (x: number, y: number) => void;
  onElegir: (i: number | null) => void;
  onMover: (i: number, dx: number, dy: number) => void;
}) {
  const imagen = usePaginaDePlantilla(id, pagina, true);
  const img = useRef<HTMLImageElement>(null);

  return (
    <Card className="p-2">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="text-2 text-muted">
          <i className="bi bi-hand-index" aria-hidden="true" /> Pulsa sobre el documento para
          colocar un campo. Con el teclado: «Agregar campo» y luego las flechas.
        </p>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost" size="sm" iconOnly
            aria-label="Página anterior"
            disabled={pagina <= 0}
            onClick={() => onPagina(pagina - 1)}
          >
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </Button>
          <span className="text-2 tabular-nums text-ink-2">Pág. {pagina + 1}/{paginas}</span>
          <Button
            variant="ghost" size="sm" iconOnly
            aria-label="Página siguiente"
            disabled={pagina >= paginas - 1}
            onClick={() => onPagina(pagina + 1)}
          >
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {tamanosFallaron && (
        <p className="mb-2 rounded-sm border border-warn/40 bg-warn/16 p-2 text-2 text-warn-ink">
          <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" /> No se pudieron leer
          los tamaños reales de las páginas: se está usando carta (612×792). Las posiciones que
          guardes ahora pueden no caer donde se ven.
        </p>
      )}

      <div className="relative overflow-hidden rounded-sm border border-line">
        {imagen.isPending ? (
          <Skeleton className="aspect-[612/792] w-full" />
        ) : imagen.isError ? (
          <div className="p-4">
            <EstadoError
              err={imagen.error}
              que="la página del documento"
              onReintentar={() => void imagen.refetch()}
            />
          </div>
        ) : (
          <>
            <img
              ref={img}
              src={imagen.data}
              alt={`Página ${pagina + 1} del PDF de la plantilla`}
              className="block w-full select-none"
              draggable={false}
              onClick={(e) => {
                const caja = e.currentTarget.getBoundingClientRect();
                onColocar(
                  (e.clientX - caja.left) / caja.width * tamano.w,
                  (e.clientY - caja.top) / caja.height * tamano.h,
                );
              }}
            />
            <div className="pointer-events-none absolute inset-0">
              {campos.map((f, i) => (f.page === pagina ? (
                <span
                  key={i}
                  className={cn(
                    'absolute -translate-y-px whitespace-nowrap rounded-xs border px-1 text-1 font-bold',
                    i === elegido
                      ? 'border-brand-ink bg-brand text-on-brand'
                      : 'border-accent-strong bg-accent text-brand',
                  )}
                  style={{ left: `${f.x / tamano.w * 100}%`, top: `${f.y / tamano.h * 100}%` }}
                >
                  {f.source === 'auto' && <i className="bi bi-gear" aria-hidden="true" />}{' '}
                  {(f.label || f.name).slice(0, 18)}
                </span>
              ) : null))}
            </div>
          </>
        )}
      </div>

      {elegido != null && campos[elegido] && campos[elegido].page === pagina && (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-2 text-ink-2">
          <span>
            <strong>{campos[elegido].label}</strong> en x {Math.round(campos[elegido].x)}, y{' '}
            {Math.round(campos[elegido].y)}
          </span>
          {/* Mover con el teclado: cuatro botones y las flechas cuando tienen el
              foco. Es lo que faltaba para poder configurar sin ratón. */}
          <span className="flex gap-1">
            {([
              ['Izquierda', -5, 0, 'bi-arrow-left'],
              ['Arriba', 0, -5, 'bi-arrow-up'],
              ['Abajo', 0, 5, 'bi-arrow-down'],
              ['Derecha', 5, 0, 'bi-arrow-right'],
            ] as const).map(([nombre, dx, dy, icono]) => (
              <Button
                key={nombre}
                variant="ghost" size="sm" iconOnly
                aria-label={`Mover ${nombre.toLowerCase()}`}
                onClick={() => onMover(elegido, dx, dy)}
                onKeyDown={(e) => {
                  const paso = e.shiftKey ? 1 : 5;
                  const mapa: Record<string, [number, number]> = {
                    ArrowLeft: [-paso, 0], ArrowRight: [paso, 0],
                    ArrowUp: [0, -paso], ArrowDown: [0, paso],
                  };
                  const d = mapa[e.key];
                  if (d) { e.preventDefault(); onMover(elegido, d[0], d[1]); }
                }}
              >
                <i className={`bi ${icono}`} aria-hidden="true" />
              </Button>
            ))}
          </span>
          <Button variant="ghost" size="sm" onClick={() => onElegir(null)}>Deseleccionar</Button>
        </p>
      )}
    </Card>
  );
}

/* ── La lista de la derecha ────────────────────────────────────────────── */

function ListaDeCampos({
  campos, pagina, elegido, onElegir, onCambiar, onQuitar, onAgregarAlCentro,
}: {
  campos: CampoDePlantilla[];
  pagina: number;
  elegido: number | null;
  onElegir: (i: number) => void;
  onCambiar: (i: number, parche: Partial<CampoDePlantilla>) => void;
  onQuitar: (i: number) => void;
  onAgregarAlCentro: () => void;
}) {
  return (
    <Card className="p-3">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-3 font-bold text-brand-ink">
          Campos <span className="font-normal text-muted">({campos.length})</span>
        </h2>
        <Button variant="secondary" size="sm" className="ml-auto" onClick={onAgregarAlCentro}>
          <i className="bi bi-plus-lg" aria-hidden="true" /> Agregar campo
        </Button>
      </div>

      {!campos.length ? (
        <p className="text-2 text-muted">
          Pulsa en el documento —o usa «Agregar campo»— para colocar el primero.
        </p>
      ) : (
        <ul className="flex max-h-[60vh] flex-col gap-2 overflow-auto">
          {campos.map((f, i) => (
            <li
              key={i}
              className={cn(
                'rounded-sm border p-2',
                i === elegido ? 'border-brand-ink bg-brand/8' : 'border-line',
                f.page !== pagina && 'opacity-60',
              )}
            >
              <div className="flex items-center gap-1.5">
                <InputSuelto
                  aria-label={`Etiqueta del campo ${i + 1}`}
                  className="min-w-0 flex-1 py-1 text-2"
                  value={f.label}
                  onFocus={() => onElegir(i)}
                  onChange={(e) => onCambiar(i, { label: e.target.value })}
                  placeholder="Etiqueta"
                />
                <Button
                  variant="ghost" size="sm" iconOnly
                  aria-label={`Quitar el campo ${f.label || i + 1}`}
                  onClick={() => onQuitar(i)}
                >
                  <i className="bi bi-x-lg text-danger-ink" aria-hidden="true" />
                </Button>
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <SelectSuelto
                  aria-label={`Origen del campo ${i + 1}`}
                  className="w-auto py-1 text-1"
                  value={f.source}
                  onChange={(e) => onCambiar(i, e.target.value === 'auto'
                    ? { source: 'auto', auto_key: f.auto_key || 'nombre' }
                    : { source: 'manual', auto_key: null })}
                >
                  <option value="manual">Lo llena quien solicita</option>
                  <option value="auto">Automático</option>
                </SelectSuelto>

                {f.source === 'auto' ? (
                  <SelectSuelto
                    aria-label={`Dato automático del campo ${i + 1}`}
                    className="w-auto py-1 text-1"
                    value={f.auto_key ?? 'nombre'}
                    onChange={(e) => onCambiar(i, { auto_key: e.target.value })}
                  >
                    {AUTOMATICOS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </SelectSuelto>
                ) : (
                  <>
                    <SelectSuelto
                      aria-label={`Tipo del campo ${i + 1}`}
                      className="w-auto py-1 text-1"
                      value={f.type}
                      onChange={(e) => onCambiar(i, {
                        type: e.target.value as CampoDePlantilla['type'],
                      })}
                    >
                      <option value="text">Texto</option>
                      <option value="date">Fecha</option>
                      <option value="textarea">Párrafo</option>
                    </SelectSuelto>
                    <label className="flex items-center gap-1 whitespace-nowrap text-1 text-ink-2">
                      <input
                        type="checkbox"
                        checked={!!f.required}
                        onChange={(e) => onCambiar(i, { required: e.target.checked })}
                      />
                      obligatorio
                    </label>
                  </>
                )}

                <label className="flex items-center gap-1 whitespace-nowrap text-1 text-ink-2">
                  tamaño
                  <InputSuelto
                    type="number" min={6} max={24}
                    aria-label={`Tamaño de letra del campo ${i + 1}`}
                    className="w-14 py-1 text-1"
                    value={f.size}
                    onChange={(e) => onCambiar(i, { size: parseFloat(e.target.value) || 10 })}
                  />
                </label>

                {f.page !== pagina && (
                  <span className="text-1 text-muted">pág. {f.page + 1}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
